// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ArtifactListCard.test.tsx
import { CircleNotch, FileText } from "@phosphor-icons/react";
import cx from "classnames";
import { useTranslation } from "react-i18next";

import { UnreadDot } from "../../../components/ask-puntt-list/UnreadDot";
import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import { type ChatArtifact } from "../artifactTypes";

/** Canvas / document row in the artifact sidebar (reviews use ReviewListCard). */
export function ArtifactListCard({
  artifact,
  isActive,
  onClick,
  testIdPrefix = "chat-artifact-row",
  hasUnread = false,
  unreadTestId,
  "data-testid": dataTestId,
}: {
  artifact: ChatArtifact;
  isActive?: boolean;
  onClick?: () => void;
  testIdPrefix?: string;
  hasUnread?: boolean;
  unreadTestId?: string;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");
  const disabled = artifact.isGenerating ?? false;
  const cardTestId = dataTestId ?? `${testIdPrefix}-${artifact.id}`;

  const ariaLabel = disabled
    ? t("artifactListCard.generatingAriaLabel", { title: artifact.title })
    : isActive
      ? t("artifactListCard.currentlyViewingAriaLabel", {
          title: artifact.title,
        })
      : t("artifactListCard.openAriaLabel", { title: artifact.title });

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-current={isActive ? "true" : undefined}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      data-artifact-kind={artifact.kind}
      data-artifact-active={isActive ? "true" : "false"}
      className={cx(
        "flex flex-col gap-2 p-3 text-left disabled:cursor-not-allowed disabled:opacity-60",
        interactiveSurfaceClasses({ interactive: true, disabled }),
        isActive && "ring-2 ring-puntt-accent-8 ring-offset-1",
      )}
    >
      <div className="flex items-stretch gap-3">
        <span
          aria-hidden="true"
          data-testid={`${cardTestId}-icon`}
          className={interactiveSurfaceIconClasses(
            "flex w-10 shrink-0 items-center justify-center self-stretch overflow-hidden rounded-md bg-puntt-accent-9 text-base-white",
          )}
        >
          {disabled ? (
            <CircleNotch
              size={18}
              className="animate-spin"
              aria-hidden="true"
            />
          ) : (
            <FileText size={18} weight="fill" aria-hidden="true" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="block truncate text-[13px] font-semibold text-puntt-neutral-gray-12">
              {artifact.title}
            </span>
            {hasUnread ? (
              <UnreadDot
                testId={unreadTestId ?? `chat-artifact-unread-${artifact.id}`}
                ariaLabel={t("artifactListCard.newDocumentAriaLabel")}
              />
            ) : null}
          </span>
          {artifact.subtitle ? (
            <span className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center text-[11px] text-puntt-neutral-gray-10"
                data-testid={`${cardTestId}-subtitle`}
              >
                {artifact.subtitle}
              </span>
            </span>
          ) : null}
        </span>
      </div>
    </button>
  );
}
